// SIM-2729: the detail panel behind the "Last written" cell.
//
// Exists because one timestamp is not checkable on its own — "hourly" beside a
// stale-looking age reads as broken even when both are correct. Every number
// here comes from RunPlan step completions, so it is what actually ran.

import type { TableRunFacts, TableRunStatus } from "@Simplicity-Education/core";
import { diffScope, sameScope } from "@Simplicity-Education/core";
import { formatDurationMinutes, formatRelativeTime } from "../../../lib/formatRelativeTime";
import { describeScope } from "../../../lib/tableRunScope";

/**
 * SIM-2742's bounded failure vocabulary, in operator words — deliberately this and not the raw
 * error string the plan step carries, which is provider-controlled and unbounded (several
 * connectors interpolate an upstream HTTP body into it) while these rows outlive Sentry retention.
 * An unrecognised kind falls back to the code itself rather than being hidden (SIM-2915).
 */
const ERROR_KIND_LABEL: Record<string, string> = {
  ps_maintenance: "PowerSchool in maintenance",
  upstream_html: "Upstream returned HTML, not data",
  invalid_json: "Upstream returned invalid JSON",
  auth: "Authentication rejected",
  not_allowed: "Not permitted for these credentials",
  not_found: "Endpoint or table not found",
  rate_limit: "Rate-limited by the source",
  upstream_4xx: "Source rejected the request",
  upstream_5xx: "Source error",
  network: "Network failure",
  timeout: "Timed out",
  bq_insert: "BigQuery insert failed",
  other: "Unclassified failure",
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-0.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-foreground">{children}</dd>
    </div>
  );
}

const CADENCE_LABEL: Record<string, string> = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  adhoc: "Manual run",
  other: "Other",
};

/**
 * SIM-2914: the filters this fetch ran under, and what moved since the one before. The count above
 * is uninterpretable without them — the same table returned 25,351 rows under `P-7D` and 6,397
 * under `P-1D` a minute apart. `null` is stated, never inferred: most rows have no stamp and always
 * will, and recomputing one from current config renders today's filters against yesterday's count.
 */
function ScopeBlock({ last, previous }: { last: TableRunFacts; previous: TableRunFacts | null }) {
  if (last.scope == null) {
    return (
      <Row label="Scope">
        <span className="text-muted-foreground">
          not recorded — this run predates the stamp, or its connector does not report one
        </span>
      </Row>
    );
  }

  const lines = describeScope(last.scope);
  const changed = diffScope(last.scope, previous?.scope ?? null);

  return (
    <Row label="Scope">
      <div className="space-y-0.5">
        {lines.length === 0 ? (
          <div className="text-muted-foreground">no filters — the whole table</div>
        ) : (
          lines.map((l) => (
            <div key={l.label} className={l.tone === "warning" ? "text-warning" : undefined}>
              {l.text}
            </div>
          ))
        )}
        {changed.length > 0 && (
          <div className="text-warning">changed since the previous run — {changed.join("; ")}</div>
        )}
      </div>
    </Row>
  );
}

/**
 * SIM-2791: what the fetch actually did, from `TableRun`. A separate block from "By cadence" above
 * on purpose — that is what the plan recorded, this is what the fetch returned, and the
 * disagreement is the finding. Counts are shown as facts; the verdict over them is computed
 * server-side from the scope stamp (SIM-2913 D2b), never here.
 */
function RunFacts({
  run,
  nowMs,
  lookbackDays,
}: {
  run: TableRunStatus;
  nowMs: number;
  lookbackDays: number;
}) {
  const last = run.lastRun;
  if (last == null) {
    return (
      <Row label="Last fetch">
        <span className="text-muted-foreground">
          {lookbackDays > 0 ? `no run recorded in the last ${lookbackDays}d` : "no run recorded"}
        </span>
      </Row>
    );
  }

  return (
    <>
      <Row label="Last fetch">
        {formatRelativeTime(Date.parse(last.at), nowMs)}
        <span className="text-muted-foreground">
          {" — "}
          {last.recordsFetched.toLocaleString()} fetched
          {last.recordsInserted !== last.recordsFetched &&
            `, ${last.recordsInserted.toLocaleString()} inserted`}
          {last.mode && ` · ${last.mode}`}
        </span>
      </Row>

      <ScopeBlock last={last} previous={run.recentRuns[1] ?? null} />

      {last.skipReason && (
        <Row label="Skipped">
          <span className="text-muted-foreground">
            the project is {last.skipReason} — this run was skipped by design, not a failure
          </span>
        </Row>
      )}

      {last.errorKind && (
        <Row label="Failure">
          <span className="text-destructive">
            {ERROR_KIND_LABEL[last.errorKind] ?? last.errorKind}
          </span>
          {last.errorStatus != null && (
            <span className="text-muted-foreground"> (HTTP {last.errorStatus})</span>
          )}
        </Row>
      )}

      {run.recentRuns.length > 1 && (
        <Row label="Recent">
          <div className="space-y-0.5">
            {run.recentRuns.map((r) => (
              <div key={r.at} className="flex gap-2">
                <span className="w-24 shrink-0 text-muted-foreground">
                  {formatRelativeTime(Date.parse(r.at), nowMs)}
                </span>
                <span>
                  {r.recordsFetched.toLocaleString()}
                  {r.errorKind && <span className="text-destructive"> failed</span>}
                  {r.skipReason && <span className="text-muted-foreground"> skipped</span>}
                  {/* Only when both runs carry a stamp. Two unstamped runs are
                      two absences of evidence, not a scope disagreement, and
                      annotating them would invent a finding. */}
                  {last.scope != null && r.scope != null && !sameScope(r.scope, last.scope) && (
                    <span className="text-muted-foreground"> · different scope</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Row>
      )}
    </>
  );
}

/**
 * States whose sentence needs no numbers. Kept as a table rather than switch
 * arms so `verdictLine` stays under the complexity cap as the vocabulary grows.
 */
const STATIC_VERDICT: Partial<Record<TableRunStatus["dot"], string>> = {
  unscheduled: "No schedule includes this table, so nothing is due.",
  paused: "The project is paused, so scheduled runs are skipped by design.",
  unknown: "The backing report is older than a full cadence, so freshness cannot be judged.",
  empty: "The last run finished but fetched no rows.",
  suspect: "Wrote on time, but what it wrote looks wrong.",
  never: "Scheduled, but no completed run has ever written it.",
  failed: "The most recent run for this table failed.",
  running: "A run is in progress and on time.",
};

/** One plain sentence explaining the dot — nothing else. */
function verdictLine(run: TableRunStatus, nowMs: number): string {
  const fixed = STATIC_VERDICT[run.dot];
  if (fixed) return fixed;

  const window = run.windowMinutes;
  const within = window == null ? "its cadence" : formatDurationMinutes(window);

  if (run.dot === "late") {
    return run.overdueByMinutes != null
      ? `Overdue by ${formatDurationMinutes(run.overdueByMinutes)} — expected within ${within}.`
      : "Overdue.";
  }
  return run.lastWrittenAt == null
    ? "On schedule."
    : `Written ${formatRelativeTime(Date.parse(run.lastWrittenAt), nowMs)}, within ${within}.`;
}

export function LastGeneratedDetail({
  run,
  nowMs,
  lookbackDays,
}: {
  run: TableRunStatus;
  /** The server's reduce-time clock, not the browser's. */
  nowMs: number;
  /** Run-history aperture, so an absent run states the window it looked in. */
  lookbackDays: number;
}) {
  const scheduled = run.scheduledCadences.map((c) => CADENCE_LABEL[c] ?? c);

  return (
    <dl className="space-y-2 text-xs">
      <Row label="Table">
        <div className="space-y-0.5 font-mono text-[11px]">
          {run.bronzeTable && (
            <div>
              <div className="font-sans text-muted-foreground">bronze</div>
              <div className="break-all">
                {run.bronzeDataset}.{run.bronzeTable}
              </div>
            </div>
          )}
          {run.silverTable ? (
            <div>
              <div className="font-sans text-muted-foreground">silver</div>
              <div className="break-all">
                {run.silverDataset}.{run.silverTable}
              </div>
            </div>
          ) : (
            <div className="font-sans text-muted-foreground">No silver table has been written.</div>
          )}
        </div>
      </Row>

      <Row label="Last written">
        {run.lastWrittenAt == null ? (
          <span className="text-muted-foreground">never</span>
        ) : (
          <>
            {new Date(run.lastWrittenAt).toLocaleString()}
            {run.lastWrittenBy === "adhoc" && (
              <span className="text-muted-foreground"> — by a manual run, not the schedule</span>
            )}
          </>
        )}
      </Row>

      <Row label="Sync schedule">{scheduled.length > 0 ? scheduled.join(", ") : "none"}</Row>

      {run.perCadence.length > 0 && (
        <Row label="By cadence">
          <div className="space-y-0.5">
            {run.perCadence.map((c) => (
              <div key={c.cadence} className="flex gap-2">
                <span className="w-20 shrink-0 text-muted-foreground">
                  {CADENCE_LABEL[c.cadence] ?? c.cadence}
                </span>
                <span>
                  {c.at == null ? "—" : formatRelativeTime(Date.parse(c.at), nowMs)}
                  {/* SIM-2915: the bounded failure kind is rendered in the
                      run-facts block below, from `TableRun.errorKind`. The
                      plan step's free-text `error` is deliberately NOT shown —
                      it is provider-controlled and unbounded. */}
                  {c.status === "failed" && <span className="text-destructive"> failed</span>}
                  {/* A cadence whose newest attempt never finished: the time above
                      is the last GOOD write, so without this the stall is
                      invisible. Impact's daily sat like this all day. */}
                  {(c.status === "pending" || c.status === "in-progress") && c.at != null && (
                    <span className="text-warning"> — last attempt did not finish</span>
                  )}
                  {c.status === "skipped" && c.at != null && (
                    <span className="text-muted-foreground"> — last attempt skipped</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Row>
      )}

      {run.inFlight && (
        <Row label="In progress">
          started {formatRelativeTime(Date.parse(run.inFlight.startedAt), nowMs)}
          {run.inFlight.late && <span className="text-warning"> — running long</span>}
        </Row>
      )}

      <RunFacts run={run} nowMs={nowMs} lookbackDays={lookbackDays} />

      <Row label="Verdict">{verdictLine(run, nowMs)}</Row>
    </dl>
  );
}
